
import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator';
import ModelMixin from '@/mixins/ModelMixin';
import ValidateableMixin from '@/mixins/ValidateableMixin';
import { StripeElementCard } from '@vue-stripe/vue-stripe';
import { PaymentUser } from '@/types/payment';
import { mutations } from '@/store/types';
import api from '@/modules/api';
import PaymentMethodCard from '../plans/PaymentMethodCard.vue';
import { getErrorMessage } from '@/utils/common';
import { stripePublishableKey } from '@/config';
import { State } from 'vuex-class';
import { DeviceInfo } from '@/types/device-info';
import CouponElementCard from '@/components/devices/plans/CouponElementCard.vue';
import { CouponInterface } from '@/types/coupons';
import { loadStripe } from '@stripe/stripe-js';
import { resolveUserCountry } from '@/utils/countries';
import { UserInfo } from '@/types/user-info';
import { termsAndConditionsURL } from '@/constants/constants';
import TermsAndConditionModal from '@/components/devices/popups/TermsAndConditionModal.vue';
import { PricingResolved } from '@/types/pricing-plan';

@Component({
    components: { StripeElementCard, PaymentMethodCard, CouponElementCard, TermsAndConditionModal },
    mixins: [ModelMixin, ValidateableMixin],
})
export default class PaymentCardModal extends Mixins<ValidateableMixin, ModelMixin<boolean>>(
    ValidateableMixin,
    ModelMixin,
) {
    @State('device')
    device!: DeviceInfo;

    @Prop({ required: true })
    stripePaymentUser!: PaymentUser | null;

    @Prop({ required: true, default: [] })
    paymentMethods!: any[];

    @Prop({ required: true })
    onSubmit!: (couponCode: string) => void;

    @Prop({ required: true })
    onRemove!: (cardId: string) => void;

    @Prop({ required: true })
    refresh!: () => void;

    @Prop({ required: true, default: false })
    freeTrial!: boolean;

    @Prop({ required: true, default: 0 })
    price!: number;

    @Prop({ required: true })
    currency!: string;

    @Prop({ required: true, default: false })
    allowCoupon!: boolean;

    @Prop({ required: true, default: true })
    allowSubmit!: boolean;

    @Prop({ required: true })
    planToPurchase!: PricingResolved

    @Prop({ required: false })
    couponAppliedDetails!: CouponInterface | null;

    @Prop({})
    user!: UserInfo;

    @Prop({})
    planTotal!: string;

    publishableKey = stripePublishableKey;
    couponCode = '';
    enableCoupon = false;
    changeCard = false;
    couponDetails: CouponInterface | null = null;
    selectedCard = '';
    termsAndConditionsURL = termsAndConditionsURL;
    termsAndConditionModal = false;
    agreeChecked = false;
    addressValues: any = {}
    tokenCreatedButton = ''
    stripe: any;
    elements: any;

    async handleCardRemove(cardId: string) {
        await this.onRemove(cardId);
        this.cancelCardChange();
        this.loadStripeElement();
    }

    onChange(cardId: string) {
        this.selectedCard = cardId;
        this.changeCard = true;
        this.loadStripeElement();
    }

    cancelCardChange() {
        this.selectedCard = '';
        this.changeCard = false;
    }

    onClose() {
        this.model = false;
    }

    async AddCard() {
        try {
            const response = await this.stripe?.confirmSetup({
                elements: this.elements,
                redirect: 'if_required'
            });
            if(response?.error) {
                Vue.toasted.error(response.error.message);
                return;
            }
            this.$store.commit(mutations.LOADING, { loading: true });
            if (response?.setupIntent) {
                const token = { id: response?.setupIntent?.payment_method }
                await this.tokenCreated(token, 'paymentId')
            }
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
        // (this.$refs.elementRef as Vue & { submit: () => boolean }).submit();
    }

    submit() {
        if (this.paymentMethods && this.paymentMethods.length > 0) {
            if (this.freeTrial && !this.agreeChecked) {
                Vue.toasted.error('Please check that you agree to our terms and conditions');
                return;
            }
            this.onSubmit(this.couponDetails ? this.couponDetails.code : '');
            return;
        }
        Vue.toasted.error('Please add card details');
    }

    async applyCoupon() {
        if (!this.couponCode) return Vue.toasted.error('Invalid code');
        try {
            this.$store.commit(mutations.LOADING, { loading: true });
            this.couponDetails = await api.validateCoupon(this.couponCode, this.device.deviceUUID, this.planToPurchase?.sourceData?.productId);
            this.couponCode = '';
            this.enableCoupon = false;
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async tokenCreated(token: any, type: string) {
        if (this.changeCard && this.selectedCard) await this.changePaymentMethod(token.id, type);
        else await this.generateClient(token.id, type);
    }

    openTermsModal() {
        this.termsAndConditionModal = true;
    }

    async changePaymentMethod(cardId: string, type: string) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.changePaymentMethod(cardId, this.selectedCard, this.addressValues, type, this.user?._id);
            await this.refresh();
            this.cancelCardChange();
            Vue.toasted.success('Changed successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async generateClient(cardId: string, type: string) {
        this.$store.commit(mutations.LOADING, { loading: true });
        try {
            await api.attachPaymentMethodToCustomer(cardId, this.addressValues, type, this.user?._id);
            await this.refresh();
            Vue.toasted.success('Added successfully');
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
    }

    async onCouponRemove() {
        this.couponDetails = null;
    }

    @Watch('model', { immediate: true })
    onModelChange(newValue: boolean, oldValue: boolean) {
        if (newValue && !oldValue) {
            this.couponCode = '';
            this.enableCoupon = false;
            this.changeCard = false;
            this.selectedCard = '';
            this.couponDetails = null;
        }
    }

    async loadStripeElement() {
        try {
            this.$store.commit(mutations.LOADING, { loading: true });
            this.stripe = await loadStripe(this.publishableKey);
            const paymentIntent = await api.createPaymentIntent();
            this.elements = this.stripe?.elements({ clientSecret: paymentIntent?.clientSecret })
            const paymentElement = this.elements?.create('payment')
            paymentElement?.mount('#payment-element');
            const addressElement = this.elements?.create('address', {
                mode: 'billing',
                autocomplete: {
                    mode: "automatic",
                },
            } as any);
            addressElement?.mount('#address-element');
            addressElement?.on('change', (event: any) => {
                this.addressValues = event.value
            });
            await resolveUserCountry();
        } catch (error) {
            Vue.toasted.error(getErrorMessage(error));
        } finally {
            this.$store.commit(mutations.LOADING, { loading: false });
        }
        this.$store.commit(mutations.LOADING, { loading: false });
    }

    @Watch('model', { immediate: true })
    loadStripeOnChangeModel() {
        if (this.model && !this.paymentMethods.length) {
            this.loadStripeElement();
        }
        this.couponDetails = this.couponAppliedDetails;
    }
}
